// Eén vraag die meerdere schermen stellen: is dit de eerste maandronde?
//
// Waarom dat uitmaakt: in ronde 1 is ÉLKE trend "nieuw" en is er niets om mee te
// vergelijken. Een NIEUW-badge op alle 1.613 regels zegt dan niets — precies de
// "badge zonder uitleg" die W8 in de dummy aanwees. De leeslaag weet het
// antwoord: /api/trends/delta zet `baseline: true` zolang er één snapshot is.
//
// De uitkomst wordt één keer opgehaald en gedeeld: het is een eigenschap van de
// dataset, niet van een scherm. Sinds 2026-08-24 geldt dat voor de HELE
// delta-respons en niet alleen voor de baseline-vlag — de pulsbalk haalde
// dezelfde vraag er met een eigen `useResource` nog een tweede keer bij
// (gemeten in het netwerkpaneel). Eén vraag, één antwoord, één plek.
//
// Een mislukte poging wordt niet gecachet: één hapering bij het opstarten mag
// niet betekenen dat het scherm de rest van de sessie zonder cijfers zit.

import { useEffect, useState } from "react";

import { api } from "./api";
import { useActieveSector } from "./sectorkeuze";
import type { TrendsDeltaResponse } from "../contracts";

// Per sector (ADR 2026-10-23-ict-los-platform B2): een tweede sector met een
// andere peildatum mag de ICT-pulsbalk niet uit de eerste ronde halen.
const cached = new Map<string, Promise<TrendsDeltaResponse>>();

function fetchDelta(sector: string | null): Promise<TrendsDeltaResponse> {
  const sleutel = sector ?? "";
  let p = cached.get(sleutel);
  if (!p) {
    p = (sector ? api.trendsDelta(undefined, undefined, sector) : api.trendsDelta()).catch((err: unknown) => {
      cached.delete(sleutel);
      throw err;
    });
    cached.set(sleutel, p);
  }
  return p;
}

/** De verschuivingen tussen maandronden; null zolang hij onderweg of mislukt is. */
export function useTrendsDelta(): TrendsDeltaResponse | null {
  const sector = useActieveSector();
  const [delta, setDelta] = useState<TrendsDeltaResponse | null>(null);
  useEffect(() => {
    let alive = true;
    fetchDelta(sector)
      .then((d) => alive && setDelta(d))
      .catch(() => alive && setDelta(null));
    return () => {
      alive = false;
    };
  }, [sector]);
  return delta;
}

/** true zolang er maar één maandsnapshot is (dus: niets te vergelijken).
 *
 *  Weten we het niet, dan gedragen we ons als ronde 1: geen badge beloven die we
 *  niet kunnen onderbouwen. */
export function useBaseline(): boolean {
  const delta = useTrendsDelta();
  return delta ? delta.baseline !== false : true;
}
